/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { bindQuerySelector, type ElementProps, jsx, setClassToken } from '@amazon/vinyl-tsx'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { isShownPath, onNavigate } from '@/router/router'
import { signedIn } from '@/auth/authRef'
import { holdLibraryForSignIn } from '@/auth/promptSignInIfAnonymous'
import { Icon } from '@/components/Icon'
import {
    clearSearch,
    getSearchDraft,
    getSearchMode,
    preloadSearchRoute,
    registerSearchInput,
    resetSearch,
    submitSearchOnPage,
    typeSearchQuery,
    showSearch as showSearchRoute,
    updateSearchQuery,
} from '@/search/searchState'
import { isTv } from '@/device/deviceConfig'
import { onKey } from '@/device/input/KeyMap'
import { Link } from '@/components/Link'
import { staticPage } from '@/staticRender'
import {
    combineData,
    data,
    type MutableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import { ProfileAvatarButton } from '@/header/ProfileAvatarButton'
import { desktopGetAppVisible$, GetAppButton } from '@/header/GetAppButton'
import { getString } from '@/header/i18n/bundle.generated'
import { canAccessAudiobooks$ } from '@/header/childProfileNavGate'
import { musicNavVisible$, podcastsNavVisible$ } from '@/header/navAvailability'
import type { DynamicMessageTrigger } from '@/messaging/dm/types'
import type { DynamicMessagingEngine } from '@/messaging/dm/dynamicMessagingEngine'
import { ctx } from '@/util/context'
import { featureEnabled$ } from '@/core/featureGate'
import { reportShellClick, reportShellNavigation } from '@/metrics/shellUiMetrics'
import type { UiClick } from '@/metrics/uiMetrics'

// Ask AI (the agent feed) is signed-in-only — the /feed route bounces anon sessions to the sign-in
// wall — and gated on the `amp` feature; shared with the mobile nav so both flip on one source.
export function askAiNavVisible$(): ObservableValue<boolean> {
    return combineData({
        signedIn: signedIn(),
        ampGate: featureEnabled$('amp'),
    }).map(({ signedIn, ampGate }) => signedIn && ampGate.result === true)
}

export type HeaderProps = ElementProps<'nav'> & {
    readonly headerMenuVisible: MutableValue<boolean>
    readonly deps: HeaderDeps
}

export type HeaderDeps = {
    readonly goodNetwork$: ObservableValue<boolean>
}

/** A header tab: GH's click on the page being left; the tab's own navigation leaves search. */
function tabClick(actionType: string, href: string) {
    return () => {
        reportShellNavigation({ actionType }, isShownPath(href))
        resetSearch()
    }
}

/** The section root itself or a path under it, so `/music` matches `/music/new` but not `/music-videos`. */
function isUnderSection(path: string, root: string): boolean {
    return path === root || path.startsWith(`${root}/`)
}

export function Header(props: HeaderProps) {
    const { headerMenuVisible, deps, ...rest } = props
    // The crawler page links the logo (home), Music and Podcasts only: Library shows unlinked, and
    // Home and Audiobooks are absent, like GH's server-rendered bar.
    const crawlerBar = staticPage
    const profileAvatar = <ProfileAvatarButton headerMenuVisible={headerMenuVisible} />

    const header = (
        <nav
            {...rest}
            aria-label={getString('dmusic_header_amazon_music_navigation_label')}
            data-nav-edge="left"
            onConnect={(view) => {
                const { add, dispose } = createDisposer()
                // The TV rail never changes shape: its search input lives on the page.
                if (isTv()) return dispose
                add(
                    getSearchMode().onData((searchMode) => {
                        setClassToken(view, 'searchMode', searchMode)
                        // The field only shows once the class lands, so it takes focus after.
                        if (searchMode) view.querySelector<HTMLElement>('.searchInput')?.focus()
                    })
                )
                add(
                    canAccessAudiobooks$.onData((available) =>
                        setClassToken(view, 'withAudiobooks', available)
                    )
                )
                // Amp off: no bottom chat input bar, so the header owns search
                // again — `ampOff` restores the persistent header search input.
                add(
                    featureEnabled$('amp').onData((gate) => {
                        if (!gate.loading) setClassToken(view, 'ampOff', gate.result === false)
                    })
                )
                return dispose
            }}
            visible={
                // Do not show the header on TVs when not signed in.
                signedIn().map((value) => !isTv() || value)
            }
        >
            <div className="headerContents">
                {/* Nav anchors carry an explicit tabIndex so Safari includes
                    them in the Tab order: WebKit drops a plain <a href> from
                    sequential focus unless "tab to links" is enabled, whereas
                    an explicitly-set tabindex is always keyboard-focusable. */}
                <Link
                    href="/"
                    title="Amazon Music"
                    {...(crawlerBar
                        ? { 'aria-label': getString('dmusic_amazon_music_home_label') }
                        : null)}
                    className="button logoBtn"
                    tabIndex={0}
                    onclick={() =>
                        reportShellNavigation({ actionType: 'goBrowse' }, isShownPath('/'))
                    }
                    navigateOptions={{ scroll: 'restore' }}
                >
                    <div className="logoLink"></div>
                </Link>
                {isTv() ? profileAvatar : null}
                {crawlerBar ? null : (
                    <Link
                        className="homeBtn button"
                        title={getString('dmusic_layout_home')}
                        href="/"
                        tabIndex={0}
                        onclick={tabClick('goBrowse', '/')}
                        navigateOptions={{ scroll: 'restore' }}
                    >
                        {/*
                          Render both glyphs and let the .selected toggle on the
                          Link choose which one is visible (filled when active,
                          outline otherwise). Saves the JS swap and keeps the
                          aria-current state authoritative.
                        */}
                        <Icon className="iconSelected" icon="homeFilled" />
                        <Icon className="iconUnselected" icon="homeOutline" />
                        <span className="label">{getString('dmusic_layout_home')}</span>
                    </Link>
                )}
                {crawlerBar ? null : (
                    <Link
                        className="askAiBtn button"
                        title={getString('dmusic_layout_chat')}
                        href="/feed"
                        tabIndex={0}
                        onclick={resetSearch}
                        visible={askAiNavVisible$()}
                    >
                        <Icon icon="chatBubble" />
                        <span className="label">{getString('dmusic_layout_chat')}</span>
                    </Link>
                )}
                <Link
                    className="musicBtn button"
                    title={getString('dmusic_browse_home_explore_music_button_label')}
                    href="/music"
                    tabIndex={0}
                    onclick={tabClick('goMusicHome', '/music')}
                    visible={crawlerBar || musicNavVisible$()}
                >
                    <Icon icon="myMusic" />
                    <span className="label">
                        {getString('dmusic_browse_home_explore_music_button_label')}
                    </span>
                </Link>
                {crawlerBar ? null : (
                    <Link
                        className="audibleBtn button"
                        title={getString('dmusic_browse_home_explore_audiobooks_button_label')}
                        href="/audiobooks"
                        tabIndex={0}
                        onclick={tabClick('goAudiobooksHome', '/audiobooks')}
                        visible={canAccessAudiobooks$}
                    >
                        <Icon icon="audibleLight" />
                        <span className="label">
                            {getString('dmusic_browse_home_explore_audiobooks_button_label')}
                        </span>
                    </Link>
                )}
                <Link
                    className="podcastsBtn button"
                    title={getString('dmusic_library_podcasts_pill_title')}
                    href="/podcasts"
                    tabIndex={0}
                    onclick={tabClick('goPodcastHomePage', '/podcasts')}
                    visible={crawlerBar || podcastsNavVisible$()}
                >
                    <Icon icon="podcasts" />
                    <span className="label">{getString('dmusic_library_podcasts_pill_title')}</span>
                </Link>
                {crawlerBar ? (
                    <span className="libraryBtn button">
                        <Icon className="iconSelected" icon="libraryFilled" />
                        <Icon className="iconUnselected" icon="library" />
                        <span className="label">{getString('dmusic_layout_library')}</span>
                    </span>
                ) : (
                    <Link
                        className="libraryBtn button"
                        title={getString('dmusic_layout_library')}
                        href="/my/library"
                        tabIndex={0}
                        onclick={(event) => {
                            tabClick('goMyMusic', '/my/library')()
                            holdLibraryForSignIn(event)
                        }}
                    >
                        <Icon className="iconSelected" icon="libraryFilled" />
                        <Icon className="iconUnselected" icon="library" />
                        <span className="label">{getString('dmusic_layout_library')}</span>
                    </Link>
                )}
                <HeaderSearch />

                <div className="sideControls">
                    <UpgradePill />
                    {crawlerBar || isTv() ? null : (
                        <GetAppButton className="getAppBtn" visible$={desktopGetAppVisible$()} />
                    )}
                    {isTv() ? null : profileAvatar}
                </div>
            </div>
        </nav>
    )

    const viewEl = bindQuerySelector(header)

    const homeBtn = header.querySelector<HTMLElement>('.homeBtn')
    const libraryBtn = viewEl('.libraryBtn')
    // Music + Podcasts + Audible are hidden on mobile, so the
    // elements may not exist when the header renders on a phone.
    const askAiBtn = header.querySelector<HTMLElement>('.askAiBtn')
    const musicBtn = header.querySelector<HTMLElement>('.musicBtn')
    const podcastsBtn = header.querySelector<HTMLElement>('.podcastsBtn')
    const audibleBtn = header.querySelector<HTMLElement>('.audibleBtn')
    const searchModeBtn = viewEl('.searchModeBtn')

    function setSelected(el: HTMLElement, selected: boolean) {
        setClassToken(el, 'selected', selected)
        // aria-current marks the active nav item for screen readers; using
        // 'page' since each nav button targets a top-level page.
        if (selected) el.setAttribute('aria-current', 'page')
        else el.removeAttribute('aria-current')
    }

    onNavigate((event) => {
        const path = event.current
        const onHome = !path || path === '/'
        if (homeBtn) setSelected(homeBtn, onHome)
        setSelected(libraryBtn, path.startsWith('/my/library') || path.startsWith('/library'))
        if (askAiBtn) setSelected(askAiBtn, path.startsWith('/feed'))
        if (musicBtn)
            setSelected(
                musicBtn,
                crawlerBar ? isUnderSection(path, '/music') : path.startsWith('/music')
            )
        if (podcastsBtn) setSelected(podcastsBtn, path.startsWith('/podcasts'))
        if (audibleBtn) setSelected(audibleBtn, path.startsWith('/audiobooks'))
        setSelected(searchModeBtn, path.startsWith('/search'))
    })

    return header
}

function HeaderSearch() {
    const view = (
        <div className="searchContainer" onConnect={() => onKey('escape', hideSearch)}>
            <button
                className="searchModeBtn button"
                onclick={() => {
                    reportShellClick({ actionType: 'goSearch' })
                    showSearch()
                }}
                onmouseover={(event: MouseEvent) => {
                    initiateSearch(event)
                    preloadSearchRoute()
                }}
                onfocus={preloadSearchRoute}
                tabIndex={0}
            >
                <Icon className="iconSelected" icon="searchFilled" />
                <Icon className="iconUnselected" icon="searchOutline" />
                <span className="label">
                    {getString('dmusic_amazon_music_search_placeholder_text')}
                </span>
            </button>
            <form
                action="#"
                autocomplete="off"
                className="searchForm"
                onsubmit={(event: Event) => {
                    event.preventDefault()
                    const query = searchInput.value.trim()
                    const click: UiClick = {
                        actionType: 'executeSearch',
                        searchQuery: query,
                    }
                    if (submitSearchOnPage(query, click)) return
                    if (query) reportShellClick(click)
                    search()
                }}
            >
                <input
                    className="searchInput"
                    placeholder={getString('dmusic_amazon_music_search_placeholder_text')}
                    type="search"
                    value=""
                    oninput={() => typeSearchQuery(searchInput.value)}
                    onmouseover={(event: MouseEvent) => {
                        initiateSearch(event)
                        preloadSearchRoute()
                    }}
                    onfocus={preloadSearchRoute}
                    onclick={() => {
                        // Pointer clicks into the input from an off-route
                        // page route to /search. Focus alone doesn't —
                        // tab-through and programmatic focus (e.g. iOS
                        // Siri hydration) shouldn't hijack navigation.
                        // Submit (Enter) is handled by the form's
                        // onsubmit, which already routes via search().
                        if (!window.location.pathname.startsWith('/search')) {
                            reportShellClick({ actionType: 'goSearch' })
                            showSearchRoute(searchInput.value.trim() || '')
                        }
                    }}
                    onConnect={(view) => {
                        const { add, dispose } = createDisposer()
                        const input = view as HTMLInputElement
                        add(registerSearchInput(input))
                        const seedFromSearchUrl = () => {
                            input.value =
                                getSearchDraft().value ||
                                (new URLSearchParams(window.location.search).get('q') ?? '')
                        }
                        add(
                            onNavigate((event) => {
                                // Leaving search clears the field; the search-
                                // mode handler below seeds it from ?q= on entry.
                                if (!event.current.startsWith('/search')) input.value = ''
                            })
                        )

                        add(
                            getSearchMode().onData((searchMode) => {
                                if (searchMode) {
                                    // Reflect the typed text or the active query, so the
                                    // header input matches a deep-linked /search?q= even
                                    // when the page's own input is absent (amp off).
                                    seedFromSearchUrl()
                                } else {
                                    view.blur()
                                    input.value = ''
                                }
                            })
                        )

                        return dispose
                    }}
                />
                <button
                    className="searchBtn"
                    tabIndex={-1}
                    aria-label={getString('dmusic_amazon_music_search_placeholder_text')}
                    title={getString('dmusic_amazon_music_search_placeholder_text')}
                >
                    <Icon icon="search" />
                </button>
            </form>
        </div>
    )

    const viewEl = bindQuerySelector(view)
    const searchInput = viewEl('.searchInput') as HTMLInputElement

    // GH reports the pointer settling on the search box, before Find opens.
    function initiateSearch(event: MouseEvent) {
        const from = event.relatedTarget
        const target = event.currentTarget
        if (from instanceof Node && target instanceof Node && target.contains(from)) return
        if (getSearchMode().value) return
        reportShellClick({ actionType: 'initiateSearch', interactionType: 'HOVER' })
    }

    // A submit before the search page mounts: the route opens on the query's results.
    function search() {
        const query = searchInput.value.trim()
        if (!window.location.pathname.startsWith('/search')) {
            showSearchRoute(query)
        } else {
            getSearchDraft().value = ''
            updateSearchQuery(query)
        }
    }

    function showSearch() {
        // Navigate to /search and pre-focus the input. Focus must stay
        // synchronous so iOS Safari opens the on-screen keyboard inside
        // the same user gesture.
        showSearchRoute(searchInput.value.trim() || '')
        searchInput.focus()
    }

    function hideSearch(event: KeyboardEvent) {
        // On the search route closing it navigates, so Back must not navigate again.
        if (getSearchMode().value) event.preventDefault()
        clearSearch()
    }

    return view
}

/**
 * Header upgrade pill (GH `HeaderRight.web.tsx`). Shown only when BOTH hold:
 * the customer may change subscription (`canChangeSubscription$` — the server's
 * capability, so no raw tier check is needed here), and a PERSISTENT_UPSELL message is active.
 * Press fires the PERSISTENT_UPSELL trigger (a critical trigger, so it always
 * surfaces the upsell modal even absent a cached message).
 *
 * Both the DM engine AND the canChangeSubscription capability are loaded off
 * the boot chunk via dynamic import() (mirroring {@link initializeDynamicMessagingLazy}).
 * Header is on the first-paint path, and `@/auth/canChangeSubscription` does
 * eager module-scope work (subscribes signedIn() + issues a capability query at
 * import), so a static import would pull it onto the entry chunk. Until both
 * modules resolve their inputs read false, so the pill stays hidden.
 */
export function UpgradePill() {
    const { errorHandler } = ctx('UpgradePill')
    const visible$: MutableValue<boolean> = data(false)
    // Captured once the lazy DM chunk resolves; the pill can only become visible
    // AFTER that (visibility gates on the engine's hasActiveMessage$), so a
    // press always finds both here.
    let engine: DynamicMessagingEngine | undefined
    let persistentUpsell: DynamicMessageTrigger | undefined

    const view = (
        <button
            className="upgradeBtn textButton"
            visible={visible$}
            onclick={() => {
                reportShellClick({
                    actionType: 'upgradePersistentUpsell',
                    contentName: 'navBarActionButton',
                })
                if (engine && persistentUpsell) {
                    engine.showDynamicMessageForTrigger(persistentUpsell).catch(errorHandler)
                }
            }}
            onConnect={() => {
                const disposer = createDisposer()
                const { add, dispose } = disposer
                // Both inputs default false, so the pill stays hidden (no
                // UNLIMITED flash) until the capability and the DM engine
                // resolve; each is subscribed as its lazy module lands.
                let canChange = false
                let hasMessage = false
                const recompute = () => {
                    visible$.value = canChange && hasMessage
                }
                // Lazily load the boot-heavy inputs off the entry chunk. The DM
                // engine and its trigger enum (`types`) share one chunk, so they
                // load together. Race-safe teardown drops each subscription if the
                // pill unmounts before its module resolves.
                let canChangeSub: Unsubscribe | undefined
                let hasMessageSub: Unsubscribe | undefined
                import('@/auth/canChangeSubscription')
                    .then((m) => {
                        if (disposer.disposed) return
                        canChangeSub = m.canChangeSubscription$.onData((allowed) => {
                            canChange = allowed
                            recompute()
                        })
                    })
                    .catch(errorHandler)
                Promise.all([
                    import('@/messaging/dm/dynamicMessagingEngine'),
                    import('@/messaging/dm/types'),
                ])
                    .then(([engineModule, typesModule]) => {
                        if (disposer.disposed) return
                        engine = engineModule.dynamicMessagingEngine
                        persistentUpsell = typesModule.DynamicMessageTrigger.PERSISTENT_UPSELL
                        hasMessageSub = engine
                            .hasActiveMessage$(persistentUpsell)
                            .onData((active) => {
                                hasMessage = active
                                recompute()
                            })
                    })
                    .catch(errorHandler)
                add(() => canChangeSub?.())
                add(() => hasMessageSub?.())
                return dispose
            }}
        >
            {getString('dmusic_upsell_button_upgrade_text')}
        </button>
    )
    return view
}
